import React, { useEffect, useState } from "react";
import { Grid, useMediaQuery } from "@mui/material";
import BreadCrumbComp from "../../components/breadCrumb";
import SimpleTable from "../../components/tables/simpleTable";
import { icons } from "../../constants";
import LaddaButton from "../../components/buttons/laddaButton";
import SimpleSelect from "../../components/selects/simpleSelect";
import { useForm } from "react-hook-form";
import { getCurrentAndNextYearOptions } from "../../utils/dateUtils";
import { StyledTooltip } from "../../components/toolltipComponent";
import { getCausesTooltipContent } from "./foreignTradeUtils";
import { useNavigate } from "react-router-dom";
import { months } from "../../constants/selectData";
import AddExecutionModal from "./addExecutionModal";
import { callApiAsync, getImportExecutionSummary, getAnnualProjectionYears } from "../../services/apiService";
import "./styles.css";

const columnsImportExecution = [
  { field: "monthName", header: "Mes" },
  { field: "plannedCount", header: "Impor. Progra." },
  { field: "executedCount", header: "Impor. Ejec." },
  { field: "executionPercentDisplay", header: "% Ejecución" },
  { field: "notExecutedDisplay", header: "No Ejecutadas dentro del mes Programado" },
  {
    field: "causesText",
    header: (
      <StyledTooltip wide title={getCausesTooltipContent()} placement="top">
        <div className="HContainer">
          <span className="no-wrap mr-10">Causas</span>
          <img
            src={icons.infoExclamationWhite}
            alt="info"
            className="btn-icon-table"
          />
        </div>
      </StyledTooltip>
    ),
  },
  { field: "actions", header: "", columnType: "actionButtons" },
];

const formatCausesText = (causes) => {
  if (!Array.isArray(causes) || causes.length === 0) return "-";
  return causes.map((c) => c.code).join(", ");
};

const ImportExecution = () => {
  const currentYear = new Date().getFullYear();
  const { control: controlTable, watch } = useForm({
    defaultValues: { year: currentYear, product: "" }
  });
  const [tableData, setTableData] = useState([]);
  const [yearsOptions, setYearsOptions] = useState(getCurrentAndNextYearOptions());
  const [openModal, setopenModal] = useState(false);
  const navigate = useNavigate();
  const [selectedRow, setSelectedRow] = useState(null);

  const yearSelected = watch("year");
  const yearValue = yearSelected?.value ?? yearSelected ?? currentYear;

  const isSmallScreen = useMediaQuery("(max-width:590px)");
  const isMediumScreen = useMediaQuery("(max-width:900px)");

  const loadSummary = () => {
    callApiAsync(
      () => getImportExecutionSummary(yearValue),
      (data) => {
        const list = Array.isArray(data) ? data : [];
        const monthlyRows = list.map((row) => {
          const notExecuted = Math.max(0, row.plannedCount - row.executedCount);
          return {
            ...row,
            id: row.month,
            causesText: formatCausesText(row.causes),
            executionPercentDisplay: row.plannedCount > 0 ? `${row.executionPercent}%` : "-",
            notExecuted,
            notExecutedDisplay: notExecuted,
            isSummaryRow: false
          };
        });

        const sum = (arr, field) => arr.reduce((a, r) => a + (r[field] ?? 0), 0);
        // % Ejecución correcto: (ejecutadas / programadas) * 100 para filas de resumen
        const executionPercentFromTotals = (planned, executed) => {
          if (!planned || planned === 0) return null;
          return Math.round((executed / planned) * 100);
        };
        const executionPercentDisplayFromTotals = (planned, executed) => {
          const pct = executionPercentFromTotals(planned, executed);
          return pct != null ? `${pct}%` : "-";
        };

        const firstSem = monthlyRows.slice(0, 6);
        const secondSem = monthlyRows.slice(6, 12);

        const firstSemPlanned = sum(firstSem, "plannedCount");
        const firstSemExecuted = sum(firstSem, "executedCount");
        const secondSemPlanned = sum(secondSem, "plannedCount");
        const secondSemExecuted = sum(secondSem, "executedCount");
        const annualPlanned = sum(monthlyRows, "plannedCount");
        const annualExecuted = sum(monthlyRows, "executedCount");

        const firstSemNotExec = Math.max(0, firstSemPlanned - firstSemExecuted);
        const secondSemNotExec = Math.max(0, secondSemPlanned - secondSemExecuted);
        const annualNotExec = Math.max(0, annualPlanned - annualExecuted);

        const firstSemRow = {
          id: "1er-sem",
          monthName: "1er Sem.",
          plannedCount: firstSemPlanned,
          executedCount: firstSemExecuted,
          executionPercent: executionPercentFromTotals(firstSemPlanned, firstSemExecuted),
          executionPercentDisplay: executionPercentDisplayFromTotals(firstSemPlanned, firstSemExecuted),
          notExecuted: firstSemNotExec,
          notExecutedDisplay: firstSemNotExec,
          causesText: "",
          isSummaryRow: true
        };

        const secondSemRow = {
          id: "2do-sem",
          monthName: "2do Sem.",
          plannedCount: secondSemPlanned,
          executedCount: secondSemExecuted,
          executionPercent: executionPercentFromTotals(secondSemPlanned, secondSemExecuted),
          executionPercentDisplay: executionPercentDisplayFromTotals(secondSemPlanned, secondSemExecuted),
          notExecuted: secondSemNotExec,
          notExecutedDisplay: secondSemNotExec,
          causesText: "",
          isSummaryRow: true
        };

        const annualRow = {
          id: "anual",
          monthName: "Anual",
          plannedCount: annualPlanned,
          executedCount: annualExecuted,
          executionPercent: executionPercentFromTotals(annualPlanned, annualExecuted),
          executionPercentDisplay: executionPercentDisplayFromTotals(annualPlanned, annualExecuted),
          notExecuted: annualNotExec,
          notExecutedDisplay: annualNotExec,
          causesText: "",
          isSummaryRow: true
        };

        // 1er Sem. después de Junio; 2do Sem. después de Diciembre; Anual al final
        setTableData([
          ...monthlyRows.slice(0, 6),
          firstSemRow,
          ...monthlyRows.slice(6, 12),
          secondSemRow,
          annualRow
        ]);
      }
    );
  };

  useEffect(() => {
    loadSummary();
  }, [yearValue]);

  useEffect(() => {
    callApiAsync(
      () => getAnnualProjectionYears(),
      (data) => {
        if (Array.isArray(data) && data.length) {
          setYearsOptions(data.map((y) => ({ name: String(y), value: Number(y) })));
        }
      }
    );
  }, []);

  const handleCloseModals = () => {
    setopenModal(false);
    setSelectedRow(null);
  };

  return (
    <div>
      <BreadCrumbComp />
      <div className="searchTitle riskAndOpportunityManagement-title">
        <h1>EJECUCIÓN DE IMPORTACIONES</h1>
      </div>

      <div className="table-container">
        <div className="HContainer j-space-between a-center">
          <h1 className="title-bio-details-chard">
            EJECUCIÓN DE IMPORTACIONES
          </h1>
          <LaddaButton
            variant="contained"
            height={isSmallScreen ? "40px" : "40px"}
            width={"200px"}
            margin={"0 5px"}
            onClick={() => {
              navigate(
                "/foreign_trade_board/importExecution/addAnnualProjectionModal",
                {
                  state: { viewOnly: false, data: { id: 0 } },
                }
              );
            }}
          >
            <div className="IHContainer align-center">
              <img
                className="btn-icon"
                src={icons.plusIcon}
                alt="icon"
                style={{ marginRight: "10px" }}
              />
              <p className="button_text_mini">Agregar Proyección anual</p>
            </div>
          </LaddaButton>
        </div>
        <br className="br-white-space" />
        <div className="mb-10">
          <div className="filter-container">
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={12} sm={2} md={2}>
                <SimpleSelect
                  name="year"
                  control={controlTable}
                  label="Año"
                  options={yearsOptions}
                  margin={"0"}
                />
              </Grid>
            </Grid></div>
          <SimpleTable
            data={tableData}
            tableStyle={{ minWidth: "870px" }}
            scrollHeight="1900px"
            columns={columnsImportExecution}
            rowClassName={(rowData) => (rowData?.isSummaryRow ? "import-execution-summary-row" : "")}
            buttons={[
              {
                type: "icon",
                icon: icons.checkList,
                hideCondition: (rowData) => rowData?.isSummaryRow,
                onClick: (rowData) => {
                  setSelectedRow(rowData);
                  setopenModal(true);
                },
              },
            ]}
          />
        </div>
      </div>
      <AddExecutionModal
        open={openModal}
        handleClose={handleCloseModals}
        data={selectedRow}
        year={yearValue}
        onSaved={loadSummary}
      />
    </div>
  );
};

export default ImportExecution;
